import React from 'react';
import { EntityProvider, useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Link } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
  EntityArgoCDHistoryCard,
  EntityArgoCDOverviewCard,
} from '@roadiehq/backstage-plugin-argo-cd';

import { useEnv } from './EnvContext';
import { EntitySparrowDeskSelectorBar } from './EntitySparrowDeskSelectorBar';
import { argoInstanceForUrl, findApp, readArgoInstances, urlsForApp } from '../coalitions';

// ArgoCD cards for env-dashboard entities. The Roadie cards read a single
// `argocd/app-name` off the entity, so on their own they always show one app
// (the US-VIRGINIA production one). Here they get a copy of the entity whose
// ArgoCD annotations point at the selected env's app and instance.
//
// `compact` (Overview tab): only the overview card, and nothing at all when the
// selected env has no ArgoCD app. Otherwise (ArgoCD tab): overview + history.
export const EntitySparrowDeskArgoCDCards = ({ compact = false }: { compact?: boolean }) => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { coalition, env } = useEnv(entity, config);

  const app = coalition ? findApp(coalition, entity) : undefined;
  const urls = coalition && app && env ? urlsForApp(app, env, coalition) : undefined;
  const instance = urls?.argocd
    ? argoInstanceForUrl(readArgoInstances(config), urls.argocd)
    : undefined;

  if (!urls?.argocd || !urls.argocdAppName) {
    if (compact) return null;
    return (
      <InfoCard title="ArgoCD">
        <Typography color="textSecondary">
          No ArgoCD app for {app?.name ?? entity.metadata.name} in {env || 'this environment'}.
        </Typography>
      </InfoCard>
    );
  }
  if (!instance) {
    // An ArgoCD Launchpad has no API access to (e.g. argocd.thrivesparrow.com).
    return (
      <InfoCard title={`ArgoCD — ${env}`}>
        <Typography>
          <Link to={urls.argocd}>Open {urls.argocdAppName} in ArgoCD</Link>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Live status isn't available here: Launchpad has no access to this ArgoCD instance.
        </Typography>
      </InfoCard>
    );
  }
  const annotations = entity.metadata.annotations ?? {};
  const scoped = {
    ...entity,
    metadata: {
      ...entity.metadata,
      annotations: {
        ...annotations,
        'argocd/app-name': urls.argocdAppName,
        'argocd/instance-name': instance.name,
        'argocd/app-namespace': annotations['argocd/app-namespace'] ?? 'argocd',
      },
    },
  };
  return (
    // Keyed by env so the cards refetch when the selection changes.
    <EntityProvider key={`${env}/${urls.argocdAppName}`} entity={scoped}>
      {compact ? (
        <EntityArgoCDOverviewCard />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EntityArgoCDOverviewCard />
          </Grid>
          <Grid item xs={12}>
            <EntityArgoCDHistoryCard />
          </Grid>
        </Grid>
      )}
    </EntityProvider>
  );
};

// ArgoCD tab: env selector on top of the selected env's cards.
export const EntitySparrowDeskArgoCDContent = () => (
  <>
    <Box mb={2}>
      <EntitySparrowDeskSelectorBar />
    </Box>
    <EntitySparrowDeskArgoCDCards />
  </>
);
